import { Document, Paragraph, Packer, TextRun, Table, TableRow, TableCell, WidthType } from 'docx';
import type {
  ParticipantExportDataOutputType as ParticipantData,
  JournalEntryDetailOutputType as JournalEntry,
  IvrQuestionAnswerOutputType as QuestionAnswer,
} from '../../graphql';
import { abbreviation } from '../abbreviation';

const P = (text: string, bold = false, alignment: 'left' | 'center' | 'right' = 'left') =>
  new Paragraph({
    children: [new TextRun({ text, bold })],
    alignment,
  });

const createTitle = (text: string) =>
  new Paragraph({
    children: [new TextRun({ text, bold: true, size: 36 })],
    alignment: 'left',
    spacing: { after: 400 },
  });

const createJournalTable = (journal: JournalEntry, entryIndex: number) => {
  const entryDate = new Date(journal.entryDate).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: `Entry #${entryIndex + 1} - ${entryDate}`,
                bold: true,
                color: 'FFFFFF',
              }),
            ],
          }),
        ],
        shading: { fill: '28A745' },
        width: { size: 70, type: WidthType.PERCENTAGE },
      }),
      new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({ text: journal.title || 'Untitled Entry', bold: true, color: 'FFFFFF' }),
            ],
          }),
        ],
        shading: { fill: '28A745' },
        width: { size: 30, type: WidthType.PERCENTAGE },
      }),
    ],
  });

  const contentRows: TableRow[] = [];

  if (journal.questionsAndAnswers && journal.questionsAndAnswers.length > 0) {
    // Questions header
    contentRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'Question', bold: true, color: 'FFFFFF' })],
              }),
            ],
            shading: { fill: '52C967' },
            width: { size: 70, type: WidthType.PERCENTAGE },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'Answer', bold: true, color: 'FFFFFF' })],
              }),
            ],
            shading: { fill: '52C967' },
            width: { size: 30, type: WidthType.PERCENTAGE },
          }),
        ],
      }),
    );

    // Questions and answers
    for (const [qIndex, qa] of journal.questionsAndAnswers.entries()) {
      const answer = qa.answerValue || '(blank)';
      contentRows.push(
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: qa.questionText })],
                }),
              ],
              shading: { fill: qIndex % 2 === 0 ? 'FFFFFF' : 'F0FFF0' },
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: answer, bold: true })],
                }),
              ],
              shading: { fill: qIndex % 2 === 0 ? 'FFFFFF' : 'F0FFF0' },
            }),
          ],
        }),
      );
    }
  } else {
    contentRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph('No questions recorded')],
            columnSpan: 2,
            shading: { fill: 'F8F9FA' },
          }),
        ],
      }),
    );
  }

  // Journal text row if present
  if (journal.text) {
    contentRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'Entry Text', bold: true })],
              }),
            ],
            shading: { fill: 'E8F5E8' },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: journal.text })],
              }),
            ],
            shading: { fill: 'E8F5E8' },
          }),
        ],
      }),
    );
  }

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [headerRow, ...contentRows],
  });
};

export async function generateJournalReportDocx(data: ParticipantData[]): Promise<Blob> {
  if (data.length === 0) {
    throw new Error('No participant data available');
  }

  const allDocumentChildren: (Paragraph | Table)[] = [];
  const studyHeader = data[0]?.studyName?.trim() || 'Study';

  // Add main title once
  allDocumentChildren.push(
    createTitle(studyHeader),
    P('Journal Q&A Report', true, 'left'),
    P(`Report Generated: ${new Date().toLocaleDateString('en-US')}`, false, 'left'),
    P(''),
  );

  // Process each participant
  for (const pt of data) {
    const initials = abbreviation(pt.participantName);

    if (pt.journalEntries.length === 0) {
      allDocumentChildren.push(
        P(`Patient Initials: ${initials}`, true),
        P('No journal data available', false),
        P(''),
      );
      continue;
    }

    allDocumentChildren.push(P(`Patient Initials: ${initials}`, true), P(''));

    for (const [index, journal] of pt.journalEntries.entries()) {
      allDocumentChildren.push(createJournalTable(journal, index), P(''));
    }
  }

  return Packer.toBlob(new Document({ sections: [{ children: allDocumentChildren }] }));
}

export function generateJournalReportCSV(data: ParticipantData[]): Blob {
  if (data.length === 0) {
    throw new Error('No participant data available');
  }

  const rows: string[] = [];

  for (const pt of data) {
    const initials = abbreviation(pt.participantName);
    const studyCode = pt.studyName?.replace(/\s+/g, '') || 'Study';
    const paddedId = pt.participantId.toString().padStart(5, '0');
    const participantCode = `${studyCode}-${paddedId}`;

    if (pt.journalEntries.length === 0) {
      rows.push(`${participantCode},${initials},,No journal data available`);
      continue;
    }

    for (const [index, journal] of pt.journalEntries.entries()) {
      const entryDate = new Date(journal.entryDate).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });

      const questionAnswers: string[] = [];
      if (journal.questionsAndAnswers && journal.questionsAndAnswers.length > 0) {
        for (const qa of journal.questionsAndAnswers) {
          const question = qa.questionText.replace(/"/g, '""');
          const answer = (qa.answerValue || '(blank)').replace(/"/g, '""');
          questionAnswers.push(`"${question}":"${answer}"`);
        }
      }

      const entryText = journal.text ? journal.text.replace(/"/g, '""') : '';
      const title = journal.title ? journal.title.replace(/"/g, '""') : 'Untitled Entry';

      rows.push(
        `${participantCode},${initials},${entryDate},"${title}","${questionAnswers.join(
          ';',
        )}","${entryText}"`,
      );
    }
  }

  return new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
}

export function journalReportFilename(data: ParticipantData[], format: 'docx' | 'csv'): string {
  if (data.length === 0) {
    return `journal-report.${format}`;
  }

  const pt = data[0]!;
  const today = new Date()
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    })
    .replace(/\//g, '.');

  const studyCode = pt.studyName?.replace(/\s+/g, '') || 'Study';
  const paddedId = pt.participantId.toString().padStart(5, '0');

  if (data.length > 1) {
    return `${studyCode}-Multiple-Participants Journal Report ${today}.${format}`;
  }

  return `${studyCode}-${paddedId} Journal Report ${today}.${format}`;
}
